/**
 * The button's own paint, in a module of its own so that a JOINED control can draw it.
 *
 * It is here rather than in `Button.tsx` for the reason `modalFooterSurface.ts` and
 * `indexTableSurface.ts` are their own files: a module that exports both a component and a
 * constant loses fast refresh for everything in it, and the linter says so. The split is
 * mechanical — nothing about these values is different from the day they sat in the component.
 */
export type ButtonVariant = 'primary' | 'secondary' | 'tertiary' | 'danger';

/**
 * Everything a button draws EXCEPT its radius, its focus ring and its surface.
 *
 * Exported so a JOINED control can draw the same button without a second copy of the paint —
 * `<SplitButton>` is the one caller, and the three it leaves out are exactly the three a
 * joined control has to own itself: the radius belongs to the group, and a segment inside a
 * clipping wrapper needs an INSET ring or the outset one is sliced off.
 *
 * <b>It is not a licence to hand-roll a button.</b> Anything that is one button is
 * `<Button>`; this exists so that two segments of ONE control cannot drift apart in the
 * brand colour, which is the failure the colour rules already name.
 */
export const BUTTON_LAYOUT =
  'inline-flex shrink-0 items-center justify-center whitespace-nowrap font-normal transition focus:outline-none disabled:cursor-not-allowed';

export type ButtonSize = 'md' | 'sm';

/**
 * The two steps, as the ONLY place either is written.
 *
 * They are a separate map rather than a second class string appended to the first, because
 * Tailwind resolves a conflict by the order of the RULES IN THE STYLESHEET, not by the order
 * of the classes in the attribute — `min-h-10 … min-h-8` does not reliably give you 32px, and
 * the one it gives you changes when the build reorders. One size, picked once, no overlap.
 *
 * **`sm` is 32px and is BELOW the 40px touch floor** reference §5 asks for, which `xs` on
 * `<ShortcutButton>` refuses to go under. It exists for ONE caller — a button in a table's
 * action gutter, product call 09 Oct 2026 — and the reasoning, including what it costs, is in
 * `reference/decisions/frontend-tables.md`. It is not the step to reach for because a button
 * looks big somewhere; a toolbar, a modal footer and a form all keep `md`.
 */
export const BUTTON_SIZES: Record<ButtonSize, string> = {
  md: 'min-h-10 gap-2 px-4 text-sm',
  sm: 'min-h-8 gap-1.5 px-3 text-xs',
};

/**
 * The standard button, unchanged — `BUTTON_LAYOUT` at the `md` step. `<SplitButton>` draws
 * this, and splitting the size out of it must not have moved it.
 */
export const BUTTON_SHAPE = `${BUTTON_LAYOUT} ${BUTTON_SIZES.md}`;

/**
 * The one button. One primary action per view; secondary is outlined, tertiary is text,
 * and destructive actions use `danger` and always confirm before they run.
 */
export const BUTTON_SURFACES: Record<ButtonVariant, string> = {
  /*
    BRAND YELLOW, resting at 500, hover 600, active 700 — never 600 at rest. Reverted here
    on 01 Oct 2026, after the button spent 29 Sep as slate-900 and then blue-500. The yellow
    is the brand and the brand is what the primary action wears; the two alternatives each
    bought a contrast ratio and each cost that.

    THE LABEL IS WHITE — product call, 01 Oct 2026, reversing the slate-900 label chosen
    hours earlier in this same comment. It is the ORIGINAL pairing, restored deliberately:
    the brand yellow with a white label is what this product's primary action has worn for
    its whole life, and it is what the reader asked for on sight.

    THE COST IS STATED RATHER THAN HIDDEN, because it is the whole of what the dark label
    bought. MEASURED, not estimated:

      rest    brand-500 #d4ac0d + white : 2.16:1      slate-900 was 8.25:1
      hover   brand-600 #be9a0b + white : 2.69:1      slate-900 was 6.64:1
      active  brand-700 #a88709 + white : 3.42:1      slate-900 was 5.22:1

    Every step is BELOW the 4.5:1 WCAG floor for text, and the resting step is below even the
    3:1 floor a large label would take. That is a brand decision outranking a contrast one,
    which is the same call the sidebar's active bar and the stepper's filled head already
    carry — and like those it is knowingly below the bar rather than accidentally.

    INTERACTION DARKENS (500 -> 600 -> 700), and with a white label that now IMPROVES
    contrast as the button is pressed — 2.16 to 3.42 — which is why the ramp was built this
    way in the first place. The dark label inverted that; white restores it.

    IF THIS EVER HAS TO CLEAR THE FLOOR, the step is brand-800 #7c6306 at 5.74:1 with white,
    which is a visibly browner yellow — or the dark label this comment just replaced. Do not
    reach for a mid-ramp compromise: nothing between 500 and 800 clears 4.5:1 with white.

    DISABLED IS GREY, NOT A PALE BRAND YELLOW — product call, 21 Oct 2026, replacing
    `brand-200` with `slate-200` and a `slate-400` label.

    The yellow said the wrong thing. Brand marks the PRIMARY ACTION, so a washed-out version
    of it read as a primary action that had merely lost some contrast — a button the reader
    keeps trying to press. Grey is the product's own "not available" everywhere else: it is
    what `secondary` and `tertiary` already disable to (`text-slate-400`), so a disabled row
    of mixed variants now reads as one state rather than three.

    The LABEL moved with the ground. It was `slate-900/40` because white on `brand-200` is
    1.26:1 and all but vanished; on `slate-200` the plain `slate-400` the other variants use
    is 2.26:1 and consistent with them. A disabled control is exempt from the contrast floor,
    not from being legible.
  */
  /*
    THE BORDER IS THE BRAND, NOT BLACK — product call, 16 Oct 2026, reversing a black
    (`slate-900`) frame tried the same day. It MOVES WITH THE GROUND, 500 -> 600 -> 700, so at
    every step it is the colour it sits on and the button reads exactly as it did before any
    border existed. That is the intent: no visible frame.

    SO WHY DRAW ONE AT ALL? Because `secondary` has carried `border border-slate-300` at the
    same `BUTTON_SIZES` all along. Without a border here the two variants were the same 40px
    box with content boxes 2px apart. Matching the border makes the two agree, and it is what
    lets `<SplitButton>` overlap its segments by exactly 1px.

    IT IS A `border`, NOT A `ring`, for the reason `<CountBadge>` already carries: a ring is a
    box-shadow drawn OUTSIDE the box, so any clipping ancestor slices it — and this surface is
    drawn inside `<SplitButton>`'s `overflow-hidden` wrapper. A border is inside the box, so
    `min-h-10` still measures 40px and the button does not grow.

    A VISIBLE yellow rim would be a DARKER step (`brand-700`), not this. It was offered and
    not taken; do not "fix" this to brand-700 on the grounds that the border cannot be seen —
    being invisible is the decision.

    BECAUSE THE FRAME CANNOT BE SEEN, `<SplitButton>` still draws its own white divider. A
    black frame briefly did that job; this one cannot.
  */
  primary:
    'border border-brand-500 bg-brand-500 text-white hover:border-brand-600 hover:bg-brand-600 active:border-brand-700 active:bg-brand-700 disabled:border-slate-200 disabled:bg-slate-200 disabled:text-slate-400',
  secondary:
    'border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 active:bg-slate-100 disabled:text-slate-400',
  tertiary:
    'text-slate-600 hover:bg-slate-100 active:bg-slate-200 disabled:text-slate-400',
  /*
    DISABLED IS THE SAME GREY AS `primary` (21 Oct 2026). It was `red-200` with the resting
    WHITE label still in force — 1.6:1, a label you cannot read on a ground that still says
    "destructive". One disabled treatment across all four variants, or a row of disabled
    buttons reports three different states.
  */
  danger:
    'bg-red-600 text-white hover:bg-red-700 active:bg-red-800 disabled:bg-slate-200 disabled:text-slate-400',
};
